import React, { useState } from "react";
import {
  Drawer,
  Typography,
  List,
  Card,
  Divider,
  Space,
  Tag,
  Collapse,
  Button,
  Empty,
  Descriptions,
  Tooltip,
} from "antd";
import {
  FileTextOutlined,
  LinkOutlined,
  DatabaseOutlined,
  SolutionOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { transformString } from "../../../../helpers/channelNameTransformation";
import prettifyString from "../../../../utils/prettifyString";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const truncatingTagStyle = {
  marginBottom: "4px",
  maxWidth: "100%",
  display: "inline-block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  verticalAlign: "bottom",
};

// Component for truncated text with expand functionality
const TruncatedText = ({ text, maxLength = 75 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!text || typeof text !== 'string') return text;
  
  if (text.length <= maxLength) {
    return <Text>{renderTextWithNewlines(text)}</Text>;
  }
  
  const truncatedText = text.substring(0, maxLength);
  
  return (
    <span>
      {isExpanded ? (
        <>
          <Text>{renderTextWithNewlines(text)}</Text>
          <Button
            type="link"
            size="small"
            style={{ padding: '0 4px', height: 'auto', fontSize: '12px' }}
            onClick={() => setIsExpanded(false)}
          >
            see less
          </Button>
        </>
      ) : (
        <>
          <Text>{renderTextWithNewlines(truncatedText)}...</Text>
          <Button
            type="link"
            size="small"
            style={{ padding: '0 4px', height: 'auto', fontSize: '12px' }}
            onClick={() => setIsExpanded(true)}
          >
            see more
          </Button>
        </>
      )}
    </span>
  );
};

// Utility function to render text with proper newline handling
const renderTextWithNewlines = (text) => {
  if (!text || typeof text !== 'string') return text;

  // Handle both literal \n strings and actual newline characters
  // First convert literal \n to actual newlines
  let processedText = text.replace(/\\n/g, '\n');
  
  // Replace single \n with spaces, but keep 2+ consecutive \n as line breaks
  processedText = processedText.replace(/\n(?!\n)/g, ' '); // Single \n becomes space
  processedText = processedText.replace(/\n{2,}/g, '\n'); // 2+ consecutive \n becomes single \n

  const lines = processedText.split('\n');
  
  return (
    <span style={{ lineHeight: '1.2' }}>
      {lines.map((line, index) => (
        <React.Fragment key={index}>
          {line}
          {index < lines.length - 1 && (
            <>
              <br />
              <hr style={{ 
                margin: '4px 0', 
                border: 'none', 
                borderTop: '1px solid #e8e8e8',
                opacity: 0.6
              }} />
            </>
          )}
        </React.Fragment>
      ))}
    </span>
  );
};

const MetaDataSection = ({ drawerData }) => {
  const titleStyle = {
    fontSize: "16px",
    margin: 0,
  };

  const sectionTitleStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "12px",
  };

  const iconStyle = {
    marginRight: "8px",
  };

  return (
    <div>
      {/* <div style={{ marginBottom: "24px" }}>
        <div style={sectionTitleStyle}>
          <FileTextOutlined style={{ ...iconStyle, color: "#1890ff" }} />
          <Title level={4} style={titleStyle}>
            Document Sources
          </Title>
        </div>

        {drawerData?.metadata?.length > 0 ? (
          drawerData?.metadata?.map((item, index) => {
            return (
              <React.Fragment key={index}>
                <Card
                  style={{
                    margin: 0,
                    padding: 0,
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                    }}
                  >
                    <LinkOutlined
                      style={{
                        color: "#1890ff",
                        marginRight: "6px",
                        marginTop: "6px",
                      }}
                    />
                    <Typography.Link
                      href={item.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontWeight: "bold",
                      }}
                    >
                      {item.source}
                    </Typography.Link>
                  </div>
                </Card>
              </React.Fragment>
            );
          })
        ) : (
          <div
            style={{
              padding: "6px 0",
              textAlign: "center",
              backgroundColor: "#f9f9f9",
              borderRadius: "8px",
              border: "1px solid #f0f0f0",
            }}
          >
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical" size="small">
                  <Typography.Text type="secondary">
                    No document sources were used in this response.
                  </Typography.Text>
                </Space>
              }
            />
          </div>
        )}
      </div> */}

      {/* <div style={sectionTitleStyle}>
        <FileTextOutlined style={{ ...iconStyle, color: "#1890ff" }} />
        <Title level={4} style={titleStyle}>
          AI Actions
        </Title>
      </div> */}

      {drawerData?.information_gathering?.length > 0 ? (
        drawerData?.information_gathering?.map((item, index) => (
          <Card
            key={index}
            style={{
              marginBottom: 26,
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.09)",
            }}
            bordered={false}
          >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              {Object.entries(item?.function_args)?.length !== 0 && (
                <div>
                  {Object.entries(item?.function_args).map(([key, value], argIndex) => {
                    if (value === null) return null;

                    // TODO: This is temportary fix, remove it when cta_id is fixed in the backend
                    if (key === "cta_id") return null;

                    return (
                      <div
                        key={key}
                        style={{
                          display: "flex",
                          marginBottom: "8px",
                          ...(!(argIndex === Object.entries(item?.function_args).length - 1) && {
                            borderBottom: "1px solid #f0f0f0",
                            paddingBottom: "8px",
                          }),
                        }}
                      >
                      <Text
                        strong
                        style={{
                          paddingRight: "8px",
                          minWidth: "100px",
                          maxWidth: "200px",
                          flexShrink: 0,
                          textTransform: "capitalize",
                        }}
                      >
                        {transformString(key)}:
                      </Text>
                        <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                          {(() => {
                            const transformedValue = transformString(value);
                            if (Array.isArray(transformedValue)) {
                              return transformedValue.map((item, idx) => {
                                // Skip empty objects
                                if (typeof item === 'object' && item !== null && Object.keys(item).length === 0) {
                                  return null;
                                }
                                // Handle objects with name and id properties
                                if (typeof item === 'object' && item !== null && item.name) {
                                  const displayText = item.name;
                                  const truncatedText = displayText.length > 40 ? displayText.substring(0, 40) + '...' : displayText;
                                  const tooltipText = Object.entries(item).map(([k, v]) => `${prettifyString(k)}: ${v}`).join('\n');
                                  return (
                                    <Tooltip key={idx} title={<div style={{ whiteSpace: 'pre-line' }}>{tooltipText}</div>}>
                                      <Tag
                                        style={truncatingTagStyle}
                                      >
                                        {truncatedText}
                                      </Tag>
                                    </Tooltip>
                                  );
                                }
                                // Handle simple string/primitive values
                                let cleanedItem = String(item).replace(/\\n/g, '\n');
                                cleanedItem = cleanedItem.replace(/\n(?!\n)/g, ' '); // Single \n becomes space
                                cleanedItem = cleanedItem.replace(/\n{2,}/g, ' '); // 2+ consecutive \n becomes space (for tags)
                                const truncatedText = cleanedItem.length > 40 ? cleanedItem.substring(0, 40) + '...' : cleanedItem;
                                return cleanedItem.length > 40 ? (
                                  <Tooltip key={idx} title={cleanedItem}>
                                    <Tag style={truncatingTagStyle}>
                                      {truncatedText}
                                    </Tag>
                                  </Tooltip>
                                ) : (
                                  <Tag key={idx} style={truncatingTagStyle}>
                                    {truncatedText}
                                  </Tag>
                                );
                              });
                            }
                            return <TruncatedText text={String(transformedValue)} />;
                          })()}
                        </div>
                      </div>
                    );
                  })}
                  <Divider style={{ margin: "12px 0" }} />
                </div>
              )}

              <div>
                {Object.entries(item?.result).map(([key, value]) => {
                  if (value === null) return null;

                  // if value is array and it is empty array then return null
                  if (Array.isArray(value) && value.length === 0) return null;

                  // TODO: This is temportary fix, remove it when tenant is fixed in the backend
                  if (key === "tenant") return null;

                  return (
                    <div
                      key={key}
                      style={{
                        display: "flex",
                        marginBottom: "4px",
                      }}
                    >
                      <Text
                        strong
                        style={{
                          paddingRight: "8px",
                          minWidth: "100px",
                          maxWidth: "200px",
                          flexShrink: 0,
                          textTransform: "capitalize",
                        }}
                      >
                        {transformString(key)}:
                      </Text>
                      <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                        {(() => {
                          if (Array.isArray(value)) {
                            return value.map((item, idx) => {
                              // Skip empty objects
                              if (typeof item === 'object' && item !== null && Object.keys(item).length === 0) {
                                return null;
                              }
                              // Handle objects with name and id properties
                              if (typeof item === 'object' && item !== null && item.name) {
                                const displayText = item.name;
                                const truncatedText = displayText.length > 40 ? displayText.substring(0, 40) + '...' : displayText;
                                const tooltipText = Object.entries(item).map(([k, v]) => `${prettifyString(k)}: ${v}`).join('\n');
                                return (
                                  <Tooltip key={idx} title={<div style={{ whiteSpace: 'pre-line' }}>{tooltipText}</div>}>
                                    <Tag
                                      style={truncatingTagStyle}
                                    >
                                      {truncatedText}
                                    </Tag>
                                  </Tooltip>
                                );
                              }
                              // Handle simple string/primitive values
                              let cleanedItem = String(item).replace(/\\n/g, '\n');
                              cleanedItem = cleanedItem.replace(/\n(?!\n)/g, ' '); // Single \n becomes space
                              cleanedItem = cleanedItem.replace(/\n{2,}/g, ' '); // 2+ consecutive \n becomes space (for tags)
                              const truncatedText = cleanedItem.length > 40 ? cleanedItem.substring(0, 40) + '...' : cleanedItem;
                              return cleanedItem.length > 40 ? (
                                <Tooltip key={idx} title={cleanedItem}>
                                  <Tag style={truncatingTagStyle}>
                                    {truncatedText}
                                  </Tag>
                                </Tooltip>
                              ) : (
                                <Tag key={idx} style={truncatingTagStyle}>
                                  {truncatedText}
                                </Tag>
                              );
                            });
                          } else if (typeof value === "object" && value !== null) {
                            return <TruncatedText text={JSON.stringify(value)} />;
                          } else {
                            return <TruncatedText text={String(value)} />;
                          }
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ textAlign: "left" }}>
                <Tag color="blue" icon={<DatabaseOutlined />}>
                  {transformString(item.function_name)}
                </Tag>
              </div>
            </Space>
          </Card>
        ))
      ) : (
        <div
          style={{
            padding: "48px 24px",
            textAlign: "center",
            background: "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
            borderRadius: "16px",
            border: "1px solid #e8e8e8",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Background decoration */}
          <div
            style={{
              position: "absolute",
              top: "-20px",
              right: "-20px",
              width: "80px",
              height: "80px",
              background: "rgba(0, 0, 0, 0.02)",
              borderRadius: "50%",
              zIndex: 0,
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "-30px",
              left: "-30px",
              width: "100px",
              height: "100px",
              background: "rgba(0, 0, 0, 0.01)",
              borderRadius: "50%",
              zIndex: 0,
            }}
          />
          
          <div style={{ position: "relative", zIndex: 1 }}>
            <div
              style={{
                width: "64px",
                height: "64px",
                margin: "0 auto 20px",
                background: "#1890ff",
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 24px rgba(24, 144, 255, 0.2)",
              }}
            >
              <DatabaseOutlined
                style={{
                  fontSize: "28px",
                  color: "white",
                }}
              />
            </div>
            
            <Title
              level={4}
              style={{
                margin: "0 0 8px 0",
                color: "rgba(0, 0, 0, 0.85)",
                fontWeight: 600,
              }}
            >
              No AI Actions Performed
            </Title>
            
            <Space direction="vertical" size="small">
              <Typography.Text
                style={{
                  color: "rgba(0, 0, 0, 0.65)",
                  fontSize: "15px",
                  lineHeight: "1.5",
                  maxWidth: "300px",
                  display: "block",
                  margin: "0 auto",
                }}
              >
                The AI responded without using any tools or performing specific actions in this conversation.
              </Typography.Text>
              
              <Typography.Text
                style={{
                  color: "rgba(0, 0, 0, 0.45)",
                  fontSize: "13px",
                  fontStyle: "italic",
                  marginTop: "8px",
                  display: "block",
                }}
              >
                Function calls and tool usage will appear here when available
              </Typography.Text>
            </Space>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetaDataSection;
